/**
 * Avida Care deep-link handler (Prompt 6B).
 *
 * When the coordinator in Avida Care opens the wizard for an RN visit,
 * they pop-open the URL `{intake_host}/wizard?draft={draft_id}&source=avida_care`.
 * This helper (called once from `pages/Wizard.jsx` on mount):
 *
 *   1. Reads `?draft=...&source=avida_care` out of the URL.
 *   2. Fetches GET /api/intake/drafts/{id} to get the seeded wizard_state.
 *   3. Hands the payload to `WizardContext.loadAvidaDraft(draft)` which
 *      merges it into state + seeds the `prefilledFields` set.
 *   4. Fires POST /api/intake/drafts/{id}/claim with the RN's user_id
 *      (read from the Phase-A parent-auth claims, falling back to a
 *      generated one for preview mode). First-touch only — subsequent
 *      claims are backend no-ops.
 *   5. Strips the URL params via history.replaceState so the RN can
 *      reload the page without re-fetching (the local wizard state is
 *      the source of truth after bootstrap).
 *
 * Failure-tolerant: if any step fails, the RN still sees the wizard —
 * they'll just need to fill the client block manually, which is the
 * same UX as a non-Avida visit.
 */
import { getStoredClaims } from "./parentAuth";

function getRnUserId() {
  // Prefer the Phase-A JWT claim's `sub` so the draft is tied to the
  // actual RN. Preview mode falls back to a stable per-browser id so
  // different tabs don't each grab a fresh identity.
  try {
    const claims = getStoredClaims();
    if (claims && claims.sub) return String(claims.sub);
  } catch { /* non-fatal */ }
  try {
    let id = window.sessionStorage.getItem("wi-pa-rn-preview-id-v1");
    if (!id) {
      id = `preview-rn-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem("wi-pa-rn-preview-id-v1", id);
    }
    return id;
  } catch {
    return "preview-rn";
  }
}

// StrictMode also re-invokes `extractAndStripDraftParams`. The first
// call strips the URL; the SECOND call sees an already-stripped URL and
// returns empty — bootstrap would then short-circuit and never hit the
// cache. Remember the params from the first extraction so the second
// invocation finds them.
let _stickyDraftParams = null;

function extractAndStripDraftParams() {
  if (typeof window === "undefined" || !window.location) return { draftId: "", source: "" };
  const url = new URL(window.location.href);
  const draftId = url.searchParams.get("draft") || "";
  const source = url.searchParams.get("source") || "";
  if (!draftId) {
    // No params in URL — fall back to the sticky params if we've already
    // bootstrapped with them in this page lifecycle.
    return _stickyDraftParams || { draftId: "", source: "" };
  }
  url.searchParams.delete("draft");
  url.searchParams.delete("source");
  try {
    const cleaned = url.pathname + (url.search || "") + (url.hash || "");
    window.history.replaceState({}, document.title, cleaned);
  } catch { /* replaceState blocked — non-fatal */ }
  _stickyDraftParams = { draftId, source };
  return _stickyDraftParams;
}

/**
 * Load (and claim) an Avida-seeded draft if the URL carries one.
 *
 * Returns:
 *   - null when the URL has no draft (normal wizard flow).
 *   - the parsed draft object on success so the caller can show a
 *     "Loaded from Avida Care" banner or log telemetry.
 *   - null on any failure (logged to console, not surfaced in UI).
 */
import axios from "axios";
import { getStoredToken } from "@/lib/parentAuth";

/**
 * Module-level cache for the in-flight bootstrap. Without this, React 18
 * StrictMode dev double-invokes the wizard's mount effect: BOTH calls
 * reach `await fetch(...)`, the first reads `res.json()`, the second
 * fails with "body stream already read". The cache key is the draftId,
 * so a normal page reload (different mount) gets a fresh fetch.
 *
 * (We use axios for the load itself because something in this preview
 * environment — maybe the global fetch interceptor + service worker
 * combo — consumes the fetch Response stream before our `res.text()`
 * runs. Axios uses XHR and doesn't share that stream.)
 */
const _bootstrapCache = new Map();  // draftId -> Promise<bootstrap result>

async function _doBootstrap(draftId, apiBase, loadAvidaDraft, onBlocked) {
  const headers = {};
  const token = getStoredToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await axios.get(
      `${apiBase}/api/intake/drafts/${encodeURIComponent(draftId)}`,
      { validateStatus: () => true, headers },  // never throw on 4xx/5xx
    );
  } catch (err) {
    console.warn("[avida] draft fetch error:", err);
    return null;
  }

  if (response.status === 403) {
    const detail = response.data?.detail || {};
    const blockPayload = {
      code: detail.code || "rn_mismatch",
      message: detail.message || "This visit is assigned to a different RN.",
      assignedTo: detail.assigned_to || "",
      youAre: detail.you_are || "",
    };
    try { onBlocked?.(blockPayload); } catch { /* non-fatal */ }
    return { __block: true, ...blockPayload };
  }
  if (response.status < 200 || response.status >= 300) {
    console.warn("[avida] draft fetch failed:", response.status);
    return null;
  }

  const draft = response.data;
  try {
    loadAvidaDraft(draft);
  } catch (err) {
    console.warn("[avida] loadAvidaDraft threw:", err);
    return null;
  }

  // Fire-and-forget claim. Don't block rendering on it.
  const rnUserId = getRnUserId();
  axios.post(
    `${apiBase}/api/intake/drafts/${encodeURIComponent(draftId)}/claim`,
    { rn_user_id: rnUserId },
  ).catch((err) => console.warn("[avida] claim fetch failed:", err));

  return draft;
}

export async function bootstrapAvidaDraft({ loadAvidaDraft, onBlocked }) {
  const { draftId, source } = extractAndStripDraftParams();
  if (!draftId || source !== "avida_care") return null;

  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  if (!apiBase) return null;

  // StrictMode dedupe: if a bootstrap for THIS draftId is already in
  // flight (or completed in this page lifecycle), return the same promise.
  // The block payload — when applicable — already fired onBlocked on the
  // first run, but we re-fire here from the cached result so the second
  // StrictMode mount also wires up state.
  if (_bootstrapCache.has(draftId)) {
    const cachedResult = await _bootstrapCache.get(draftId);
    if (cachedResult && cachedResult.__block) {
      try { onBlocked?.(cachedResult); } catch { /* non-fatal */ }
    }
    return cachedResult;
  }
  const promise = _doBootstrap(draftId, apiBase, loadAvidaDraft, onBlocked);
  _bootstrapCache.set(draftId, promise);
  return promise;
}

/**
 * After Step 10 PDFs generate successfully, notify Avida Care.
 *
 * The wizard calls this with the generator's `job_id`. We tell the
 * backend to build the completion payload + fire the outbound 3-retry
 * webhook chain. Returns the queued acknowledgement; the RN isn't
 * blocked by webhook completion.
 */
export async function completeAvidaDraft(draftId, jobId) {
  if (!draftId || !jobId) return null;
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  if (!apiBase) return null;
  try {
    const res = await fetch(
      `${apiBase}/api/intake/drafts/${encodeURIComponent(draftId)}/complete`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_id: jobId }),
      },
    );
    if (!res.ok) {
      console.warn("[avida] complete request failed:", res.status);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.warn("[avida] complete request error:", err);
    return null;
  }
}


// ─── Standalone (orphan) intake support ────────────────────────────────
// When the RN is in the field with a patient who isn't yet seeded by
// Avida (or assignment fails / network glitches), she completes the
// wizard "standalone": the full payload is POSTed to /api/intake/standalone,
// which produces an orphan draft a coordinator will reconcile later.
// On submit failure we queue the payload to localStorage so a later
// online session can flush it.

const STANDALONE_QUEUE_KEY = "wi-pa-standalone-queue-v1";

function readQueue() {
  try {
    const raw = window.localStorage.getItem(STANDALONE_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writeQueue(rows) {
  try { window.localStorage.setItem(STANDALONE_QUEUE_KEY, JSON.stringify(rows)); }
  catch { /* non-fatal — a full quota means we drop this attempt */ }
}

/**
 * Submit a fully-filled standalone wizard payload.
 *
 * Behaviour:
 *  - On success → returns `{ok: true, draft_id, deduped, queued: false}`.
 *  - On failure (network down, 5xx) → enqueues the payload to localStorage
 *    so a later online session can flush it. Returns `{ok: false, queued: true}`.
 *  - The caller should NOT block the RN — show a "1 standalone pending sync"
 *    badge and let her continue. PDFs work locally regardless of sync status.
 */
export async function submitStandaloneIntake({ jobId, rnUserId, wizardState, capturedAt }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  if (!apiBase) {
    enqueueStandalone({ jobId, rnUserId, wizardState, capturedAt });
    return { ok: false, queued: true, reason: "no_backend_url" };
  }
  const body = {
    job_id: jobId,
    rn_user_id: rnUserId || "",
    wizard_state: wizardState || {},
    captured_at: capturedAt || new Date().toISOString(),
  };
  try {
    const res = await fetch(`${apiBase}/api/intake/standalone`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      enqueueStandalone(body);
      return { ok: false, queued: true, status: res.status };
    }
    const ack = await res.json();
    return { ok: true, queued: false, ...ack };
  } catch (err) {
    enqueueStandalone(body);
    return { ok: false, queued: true, reason: err?.message || "fetch_error" };
  }
}

function enqueueStandalone(payload) {
  const rows = readQueue();
  // Dedupe by job_id — a retry of the same submit shouldn't grow the queue.
  const others = rows.filter((r) => r.job_id !== payload.job_id);
  others.push({ ...payload, queued_at: new Date().toISOString() });
  writeQueue(others);
}

export function getStandaloneQueueCount() {
  return readQueue().length;
}

/**
 * Try to flush every queued standalone payload. Called on page load
 * + on the `online` event. Returns `{flushed, remaining}`.
 */
export async function flushStandaloneQueue() {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const rows = readQueue();
  if (!apiBase || rows.length === 0) {
    return { flushed: 0, remaining: rows.length };
  }
  const survivors = [];
  let flushed = 0;
  for (const row of rows) {
    try {
      const res = await fetch(`${apiBase}/api/intake/standalone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(row),
      });
      if (res.ok) { flushed += 1; continue; }
      survivors.push(row);
    } catch {
      survivors.push(row);
    }
  }
  writeQueue(survivors);
  return { flushed, remaining: survivors.length };
}
